import randomstring from 'randomstring';
import React, { useCallback, useState } from 'react';

import { BasicModal, FullWidthBasicModal } from '@DesignSystem/modals';
import {
  DEFAULT_PYTHON_PANEL_CODE,
  DEFAULT_PYTHON_PANEL_NAME
} from '@mpm-druid/constants';
import { Panel } from '@mpm-druid/types';
import { useModelViewStore } from '../modelViewStore';
import { PanelModalContent } from './PanelModalContent';
import {
  SubmitPanelPayload,
  SubmitPanelProvider,
  useSubmitPanelListener,
  useSubmitPanelTrigger
} from './SubmitPanelContext';

type PanelModalProps = {
  onClose: () => void;
  panel?: Panel;
  sectionId: string;
};

type PanelData = Panel['data'];

function PanelModalWithContext({ onClose, panel, sectionId }: PanelModalProps) {
  const [shouldDisplayConfirmModal, setShouldDisplayConfirmModal] = useState(
    false
  );
  const submitPanel = useSubmitPanelTrigger();
  const addSectionPanel = useModelViewStore(state => state.addSectionPanel);
  const editSectionPanel = useModelViewStore(state => state.editSectionPanel);

  const [id] = useState(() => {
    return panel ? panel.id : `mpm_panel_${randomstring.generate(6)}`;
  });
  const [panelData, setPanelData] = useState<PanelData>(() => {
    if (panel) {
      return panel.data;
    }

    return {
      type: 'python',
      config: {
        code: DEFAULT_PYTHON_PANEL_CODE
      }
    };
  });
  const [panelName, setPanelName] = useState(
    panel ? panel.name : DEFAULT_PYTHON_PANEL_NAME
  );

  const openConfirmModal = () => {
    if (panelData !== panel?.data) {
      setShouldDisplayConfirmModal(true);
      return;
    }

    onClose();
  };

  const isEditing = !!panel;

  // Submit logic (interceptors)
  const handleSubmit = async () => {
    submitPanel(
      {
        id,
        name: panelName,
        data: panelData
      },
      isEditing ? 'update' : 'create'
    );
  };

  const handleFinalSubmit = useCallback(
    (payload: SubmitPanelPayload) => {
      if (payload.action === 'update') {
        editSectionPanel(sectionId, payload.panel);
      } else {
        addSectionPanel(sectionId, payload.panel);
      }

      onClose();
    },
    [addSectionPanel, editSectionPanel, onClose, sectionId]
  );

  useSubmitPanelListener(handleFinalSubmit);

  return (
    <>
      <FullWidthBasicModal
        className=""
        content={
          <PanelModalContent
            data={panelData}
            id={id}
            isEditing={isEditing}
            name={panelName}
            onDataChange={setPanelData}
            onNameChange={setPanelName}
          />
        }
        customHeaderContent={null}
        data-test="panel-modal"
        footer={null}
        headerTitle={false}
        isPrimaryButtonDisabled={false}
        isPrimaryButtonLoading={false}
        onBackButtonClick={onClose}
        onClose={openConfirmModal}
        onPrimaryButtonClick={handleSubmit}
        onSecondaryButtonClick={onClose}
        open
        primaryButtonText={isEditing ? 'Update' : 'Add'}
        secondaryButtonText="Cancel"
        stepActiveIndex={0}
        stepAmount={0}
        title={isEditing ? 'Edit Panel' : 'Add Panel'}
        titleMaskTest={false}
        variant="base"
        withBackButton
        withoutFooter={false}
      />

      {shouldDisplayConfirmModal ? (
        <BasicModal
          open
          onClose={() => setShouldDisplayConfirmModal(false)}
          title="Are you sure you want to leave?"
          content={
            <div>You have unsaved changes. Are you sure you want to leave?</div>
          }
          primaryButtonText="Confirm"
          secondaryButtonText="Cancel"
          onSecondaryButtonClick={() => setShouldDisplayConfirmModal(false)}
          onPrimaryButtonClick={onClose}
        />
      ) : null}
    </>
  );
}

export function PanelModal(props: PanelModalProps) {
  return (
    <SubmitPanelProvider>
      <PanelModalWithContext {...props} />
    </SubmitPanelProvider>
  );
}
